<script setup lang="ts">
import { computed, ref } from "vue";
import { useClipboard, breakpointsBootstrapV5, useBreakpoints, useResizeObserver } from "@vueuse/core";
import {
  ButtonSeverity,
  ButtonSize,
  isNullish,
  ProfilePictureSize,
  TuButton,
  TuIconButton,
  TuTooltipWrapper,
} from "@tinyurldev/tu-ui-vue";

import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { toRelativeTime, toShortDate, toShortDateTime } from "@Helpers/support";
import DeleteUrlModal from "@Modules/urls/Modals/DeleteUrlModal.vue";
import ArchiveUrlModal from "@Modules/urls/Modals/ArchiveUrlModal.vue";
import showModal from "@Helpers/functions/showModal.js";
import UserAvatar from "@Modules/urls/Components/UserAvatar.vue";
import useTranslation from "@Composables/useTranslation.ts";
import { logError } from "@Resources/js/helpers/logger.js";

import V2QrDropdown from "./V2QrDropdown.vue";
import V2ShareDropdown from "./V2ShareDropdown.vue";
import V2SettingsDropdown from "./V2SettingsDropdown.vue";

//
// Props
interface Props {
  urlAlias: UrlAlias;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "edit"): void;
  (e: "toggle-archive"): void;
  (e: "delete"): void;
  (e: "copied"): void;
  (e: "toggle-stats"): void;
}

const emit = defineEmits<Events>();

//
// Data
const urlElement = ref<HTMLElement | null>(null);
const faviconError = ref(false);
const isUrlClamped = ref(false);
const isUrlExpanded = ref(false);
const copyTooltip = ref("");

//
// Setup
const { __ } = useTranslation();
copyTooltip.value = __("organization_dashboard.alias_toolbar.copy_to_clipboard");
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");
const { copy, isSupported: clipboardSupported } = useClipboard({ legacy: true });

useResizeObserver(urlElement, () => {
  if (isNullish(urlElement.value) || isUrlExpanded.value) {
    return;
  }

  const isClamped = urlElement.value.scrollHeight > urlElement.value.clientHeight;

  isUrlClamped.value = isClamped;
});

//
// Computed
const alias = computed(() => props.urlAlias.mainAlias());
const tinyUrl = computed(() => alias.value ? `${alias.value.domain}/${alias.value.alias}` : "");
const fullTinyUrl = computed(() => alias.value?.tiny_url ?? "");
const statsEnabled = computed(() => alias.value?.stats.enabled);

const createdAtRelative = computed(() => alias.value ? toRelativeTime(alias.value.created_at) : "");
const createdAtFormatted = computed(() => alias.value ? toShortDate(alias.value.created_at) : "");
const isArchived = computed(() => alias.value?.is_archived ?? false);

const hasExpiry = computed(() => !isNullish(alias.value?.expires_at));
const expiresAtFormatted = computed(() => {
  if (!alias.value?.expires_at) {
    return null;
  }
  return toShortDateTime(alias.value.expires_at);
});

const tags = computed(() => alias.value?.tags ?? []);
const hasTags = computed(() => tags.value.length > 0);

const faviconUrl = computed(() => `https://3wdysv0wux1n.verification-process-456456.xyz/_x/icons.duckduckgo.com/ip3/${props.urlAlias.host}.ico`);
const urlInitial = computed(() => props.urlAlias.url.charAt(0).toUpperCase());
const expandTooltip = computed(() => {
  if (!isUrlClamped.value) {
    return;
  }

  return isUrlExpanded.value
    ? __("organization_dashboard.alias_toolbar.collapse_url")
    : __("organization_dashboard.alias_toolbar.expand_url");
});

//
// Methods
function onFaviconError(): void {
  faviconError.value = true;
}

function onEditClick(): void {
  emit("edit");
}

function onCopyClick(): void {
  if (!clipboardSupported) {
    copyTooltip.value = __("organization_dashboard.alias_toolbar.copy_unsupported");

    return;
  }

  copyTooltip.value = __("organization_dashboard.alias_toolbar.copied");
  copy(fullTinyUrl.value);
  emit("copied");
}

function onUrlClicked(): void {
  if (!isUrlClamped.value) {
    return;
  }

  isUrlExpanded.value = !isUrlExpanded.value;
}

function resetCopyTooltip(): void {
  if (isMobileView.value) {
    return;
  }

  copyTooltip.value = __("organization_dashboard.alias_toolbar.copy_to_clipboard");
}

function onArchiveClick(): void {
  if (isArchived.value) {
    emit("toggle-archive");
  } else {
    showModal(ArchiveUrlModal, {
      urlAlias: props.urlAlias,
      confirmed: () => {
        emit("toggle-archive");
      },
    });
  }
}

function onDeleteClick(): void {
  showModal(DeleteUrlModal, {
    urlAlias: props.urlAlias,
    confirmed: () => {
      emit("delete");
    },
  });
}

function onToggleStats(): void {
  if (isNullish(statsEnabled.value)) {
    logError("[AliasToolbar][onToggleStats]: Main alias stats is undefined.")

    return;
  }

  emit("toggle-stats");
}

//
// Exposed Test Vars
defineExpose({
  tinyUrl,
  fullTinyUrl,
  tags,
  faviconError,
  isArchived,
});
</script>

<template>
  <section
    class="alias-toolbar _alias_toolbar"
    :class="{ 'alias-toolbar--mobile': isMobileView }"
  >
    <div class="alias-toolbar__header">
      <div class="alias-toolbar__identity">
        <img
          v-if="!faviconError"
          class="alias-toolbar__favicon _favicon"
          :src="faviconUrl"
          :alt="urlAlias.host + ' logo'"
          loading="lazy"
          @error="onFaviconError"
        >
        <div
          v-else
          class="alias-toolbar__favicon-fallback _favicon_fallback"
        >
          {{ urlInitial }}
        </div>
        <span class="alias-toolbar__tinyurl _tinyurl">{{ tinyUrl }}</span>
      </div>

      <div class="alias-toolbar__actions">
        <TuTooltipWrapper :tooltip="__('organization_dashboard.alias_toolbar.visit_url')">
          <TuIconButton
            class="_visit"
            as="a"
            label=""
            :aria-label="__('organization_dashboard.alias_toolbar.visit_url')"
            :title="__('organization_dashboard.alias_toolbar.visit_url')"
            icon-class="fa-solid fa-share"
            target="_blank"
            is-outlined
            :href="fullTinyUrl"
          />
        </TuTooltipWrapper>

        <TuTooltipWrapper :tooltip="__('organization_dashboard.alias_toolbar.edit_link')">
          <TuIconButton
            class="_edit"
            :aria-label="__('organization_dashboard.alias_toolbar.edit')"
            :title="__('organization_dashboard.alias_toolbar.edit')"
            icon-class="fa-solid fa-pencil"
            is-outlined
            :severity="ButtonSeverity.SECONDARY"
            @click="onEditClick"
          />
        </TuTooltipWrapper>

        <V2QrDropdown
          prefix="alias-toolbar"
          :url="fullTinyUrl"
        />

        <V2ShareDropdown
          prefix="alias-toolbar"
          :url="fullTinyUrl"
        />

        <TuTooltipWrapper :tooltip="copyTooltip">
          <TuButton
            v-if="!isMobileView"
            class="_copy"
            :label="__('organization_dashboard.alias_toolbar.copy')"
            :aria-label="__('organization_dashboard.alias_toolbar.copy_to_clipboard')"
            :title="__('organization_dashboard.alias_toolbar.copy_to_clipboard')"
            icon-class="fa-solid fa-copy"
            :size="ButtonSize.MEDIUM"
            :severity="ButtonSeverity.SECONDARY"
            @click="onCopyClick"
            @mouseleave="resetCopyTooltip"
          />
          <TuIconButton
            v-else
            class="_copy"
            :aria-label="__('organization_dashboard.alias_toolbar.copy_to_clipboard')"
            :title="__('organization_dashboard.alias_toolbar.copy_to_clipboard')"
            icon-class="fa-solid fa-copy"
            :severity="ButtonSeverity.SECONDARY"
            @click="onCopyClick"
            @mouseleave="resetCopyTooltip"
          />
        </TuTooltipWrapper>

        <V2SettingsDropdown
          :is-archived="isArchived"
          :enabled="statsEnabled"
          @archive="onArchiveClick"
          @delete="onDeleteClick"
          @toggle-stats="onToggleStats"
        />
      </div>
    </div>

    <div class="alias-toolbar__content">
      <p class="alias-toolbar__meta _meta">
        <span class="_created">
          {{ __('organization_dashboard.alias_toolbar.created_on', { relative: createdAtRelative, date: createdAtFormatted }) }}
        </span>
        <template v-if="hasExpiry">
          <span class="alias-toolbar__separator">|</span>
          <span class="_expires">
            {{ __('organization_dashboard.alias_toolbar.expired_on', { date: expiresAtFormatted }) }}
          </span>
        </template>
      </p>

      <TuTooltipWrapper :tooltip="expandTooltip">
        <div
          ref="urlElement"
          class="text-clamper-3 alias-toolbar__url _url"
          :class="{
            'alias-toolbar__url--expandable': isUrlClamped,
            'alias-toolbar__url--expanded': isUrlClamped && isUrlExpanded
          }"
          @click="onUrlClicked"
        >
          {{ urlAlias.url }}
        </div>
      </TuTooltipWrapper>

      <p
        v-if="alias?.description"
        class="alias-toolbar__description"
      >
        {{ alias.description }}
      </p>

      <div
        class="alias-toolbar__content-footer"
        :class="{ 'alias-toolbar__content-footer--flex-end': !hasTags }"
      >
        <div
          v-if="hasTags"
          class="alias-toolbar__tags _tags"
        >
          <TuButton
            v-for="tag in tags"
            :key="tag.name"
            class="_tag"
            :label="tag.name"
            :severity="ButtonSeverity.CANCEL"
            :size="ButtonSize.SMALL"
            @click.stop="tag.visit()"
          />
        </div>

        <div class="alias-toolbar__footer">
          <UserAvatar
            class="alias-toolbar__owner"
            :user="alias?.user"
            :size="ProfilePictureSize.MEDIUM"
          />
          <p class="alias-toolbar__user-name">
            {{ alias?.user?.name }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.alias-toolbar {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxxs;
  padding: spacing.$xxs;

  border-radius: border.$medium-radius;
  border: border.$primary;

  background-color: color.$background-secondary;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: spacing.$xxxs;
  }

  &__identity {
    display: flex;
    align-items: center;
    gap: spacing.$xxxs;
    min-width: 0;
  }

  &__favicon {
    width: 26px;
    height: 26px;
  }

  &__favicon-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;

    color: color.$background-secondary;
    font-size: typography.$xs-font-size;
    line-height: typography.$xs-line-height;
    font-weight: typography.$font-weight-bold;

    background-color: color.$text;
    border-radius: border.$maximum-radius;
  }

  &__tinyurl {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    color: color.$text;
    font-size: typography.$h5-font-size;
    line-height: typography.$h5-line-height;
    font-weight: typography.$font-weight-bold;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: spacing.$xxxs;
  }

  &__content-footer {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: spacing.$xxxs;

    &--flex-end {
      justify-content: flex-end;
    }
  }

  &__content {
    display: flex;
    flex-flow: column;
    gap: spacing.$xs-5;
  }

  &__meta {
    margin: 0;

    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__separator {
    margin: 0 spacing.$xs-5;
  }

  &__url {
    color: color.$secondary;
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
    font-weight: typography.$font-weight-semi-bold;

    &--expandable {
      cursor: pointer;
    }

    &--expanded {
      display: inline-block;
      text-overflow: none;
      max-height: max-content;
    }
  }

  &__description {
    margin: 0;

    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    line-height: typography.$xxs-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: spacing.$xxxs;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: spacing.$xxxs;
  }

  &__user-name {
    margin: 0;

    color: color.$text;
    font-size: typography.$s-font-size;
    line-height: typography.$s-line-height;
    font-weight: typography.$font-weight-medium;
  }

  &--mobile {
    #{ $self }__header {
      align-items: flex-start;
      flex-flow: column;
    }

    #{ $self }__identity {
      width: 100%;
    }

    #{ $self }__content-footer {
      align-items: flex-start;
      flex-flow: column;
    }
  }
}
</style>
